import React from 'react'
import styled, { css } from 'styled-components'

import { FieldMetadata, FieldProps, useFieldMetadata } from '../FieldMetadata'
import { getFont } from '../Typography/Typography'
import { CustomStyleOverrideProps } from './types'
import { getBorderColor, getBrandPrimaryColor, getDangerColor } from './utils'
import { getBorderRadius } from './utils'

export interface FormTextAreaProps
  extends FieldProps,
    React.ComponentPropsWithoutRef<'textarea'> {
  /** @deprecated use errorMessage instead */
  error?: string
  baseOverrides?: Partial<CustomStyleOverrideProps>
}

const TextArea = styled.textarea<{
  labelVariant?: FieldProps['labelVariant']
  baseOverrides?: Partial<CustomStyleOverrideProps>
  hasError?: boolean
}>`
  background-color: var(--surface-primary);
  border: 1px solid ${({ baseOverrides, hasError }) => (hasError ? getDangerColor(baseOverrides) : getBorderColor(baseOverrides))};
  border-radius: ${({ baseOverrides }) => getBorderRadius(baseOverrides)};
  -webkit-box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
  box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
  box-shadow: none;
  box-sizing: border-box;
  color: var(--text-primary);
  display: block;
  font-family: ${getFont('body')};
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  min-height: 50px;
  line-height: 1.42857143;
  padding: 6px 12px;
  resize: vertical;
  -webkit-transition: border-color 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;
  -o-transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, background 0.2s ease,
    color 0.2s ease;
  width: 100%;

  &:focus {
    border-color: ${({ baseOverrides }) => getBrandPrimaryColor(baseOverrides)};
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.08), 0 0 8px ${({ baseOverrides }) => getBrandPrimaryColor(baseOverrides)};
    outline: none;
  }

  &:disabled {
    background-color: var(--surface-secondary);
  }

  ${(props) =>
    props.labelVariant === 'inset' &&
    css`
      padding-top: 20px;
    `}
`

const FormTextArea = React.forwardRef<HTMLTextAreaElement, FormTextAreaProps>(
  ({ error, errorMessage, ...props }, ref) => {
    const { fieldProps, ...fieldMetadataProps } = useFieldMetadata({
      ...props,
      errorMessage: errorMessage
        ? errorMessage
        : typeof error === 'string'
          ? error
          : undefined,
    })
    const hasError = !!errorMessage || !!error
    return (
      <FieldMetadata
        {...fieldMetadataProps}
        required={props.required}
        baseOverrides={props.baseOverrides}
        maxLength={props.maxLength}
        characterCount={
          typeof props.value === 'number' ? props.value : props.value?.length
        }
      >
        <TextArea ref={ref} {...fieldProps} {...props} hasError={hasError} />
      </FieldMetadata>
    )
  }
)

export default FormTextArea
